import DeletedWarning from "@/components/DeletedWarning";
import PageHeader from "@/components/PageHeader";
import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { PROJECT_STATUS_DELETED } from "@/schemas/project";
import { ApiError } from "@/services/api";
import {
  ReadProject,
  UpdateRoleBasedProject,
  UpdateDepartmentBasedProject,
} from "@/types/project";
import { useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Undo2 } from "lucide-react";
import { useProjectBreadcrumb } from "../hooks/useProjectBreadcrumb";
import { useProjectActions } from "../hooks/useProjectActions";
import { ProjectService } from "../service/project";
import ProjectEditorForm from "./ProjectEditorForm";
import ProjectEndDateUpdateDialog from "./ProjectEndDateUpdateDialog";
import ProjectRestoreConfirmDialog from "./ProjectRestoreConfirmDialog";
import { ProjectCommentsSection } from "./ProjectCommentsSection";
import ProjectBasicView from "./ProjectBasicView";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { ProjectDetailsAndNavigation } from "./ProjectDetailsAndNavigation";

export default function ProjectEditor() {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const queryProject = () => ProjectService.get(projectId);
  const { toast } = useToast();
  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.Projects,
  );
  const permissions = useModulePermissions(Module.Projects);

  const [updateEndDateDialogOpen, setUpdateEndDateDialogOpen] = useState(false);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);

  const projectQuery = useQuery({
    queryKey: [Module.Projects, projectId],
    queryFn: queryProject,
  });

  const { canRestore } = useProjectActions(projectId, projectQuery.data);

  const mutateProject = async (
    values: UpdateRoleBasedProject | UpdateDepartmentBasedProject,
  ): Promise<ReadProject> => ProjectService.patch(projectId, values);

  const projectMutation = useMutation<
    ReadProject,
    ApiError,
    UpdateRoleBasedProject | UpdateDepartmentBasedProject
  >({
    mutationFn: mutateProject,
    onSuccess: (result) => {
      toast({
        title: t("projects.success.update"),
      });
      updateQueryDataForExistingItem(result);
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("projects.errors.update"),
        description,
        variant: "destructive",
      });
    },
  });

  const breadcrumbItems = useProjectBreadcrumb(projectQuery);

  if (!permissions.projects_read_full) {
    return <ProjectBasicView />;
  }

  return (
    <>
      <PageHeader
        breadcrumbItems={breadcrumbItems}
        title={t("projects.title.edit")}
      />
      <div className="space-y-2">
        <ProjectDetailsAndNavigation showEditButton={false} />
        <DeletedWarning
          show={projectQuery.data?.status === PROJECT_STATUS_DELETED}
          description={t("projects.deleted.description")}
          actions={
            canRestore && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRestoreDialogOpen(true)}
              >
                <Undo2 className="mr-2 h-4 w-4" />
                {t("projects.actions.restore.title")}
              </Button>
            )
          }
        />
        {projectQuery.isFetching && (
          <Spinner label={t("projects.loading")} className="w-full h-full " />
        )}
        {!projectQuery.isFetching && (
          <>
            <ProjectEditorForm
              onSubmit={projectMutation.mutateAsync}
              project={projectQuery.data}
              disabled={
                projectMutation.isPending ||
                !projectQuery.data?.is_editable ||
                !permissions.can_update
              }
            />
            <div className="space-y-2 mt-2">
              <ProjectCommentsSection
                comments={projectQuery.data?.comments || []}
                isPending={projectQuery.isLoading}
              />
            </div>
          </>
        )}
      </div>
      <ProjectEndDateUpdateDialog
        open={updateEndDateDialogOpen}
        onOpenChange={setUpdateEndDateDialogOpen}
        value={projectQuery.data?.end_date}
      />
      <ProjectRestoreConfirmDialog
        open={restoreDialogOpen}
        onOpenChange={setRestoreDialogOpen}
        projectId={projectId}
        project={projectQuery.data}
      />
    </>
  );
}
